import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import store from 'redux/store'
import client from 'api/client'
import { authMerge } from 'redux/actions'
import { UserService } from 'api/clients/UserService'
import { removeWorkspaceInviteToken } from 'utils/workspaceUtils'
import { findPrivateWorkspaces, isAlmanacCoreWorkspace, companyDetailToWorkspace } from 'utils/workspaceUtilsSignup'
import { reportError } from 'utils/error'
import { Workspace } from 'types'
import { UserClient } from 'api/clients/UserClient'
import { useRootStore } from 'stores/RootStore'
import { useOnboardingTracking } from 'hooks/useOnboardingTracking'
import { SIGNED_UP_EVENT, TRIAL_GRANTED, trackEvent } from 'utils/trackingUtils'
import { useSearchParams } from 'hooks/useSearchParams'

/**
 * `useSignUp` merges user auth and retrieves (or creates) a *landing workspace*,
 * returning it but *not* redirecting.
 */
export function useSignUp() {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const { featureFlags } = useRootStore()
  const { trackOnboardingWorkspaceCreated, trackEmailSubmitted, trackEmailVerified } = useOnboardingTracking()
  const params = useSearchParams()
  const isCampaigns = params.get('campaigns') === 'true'

  /**
   * - Merge auth in Redux
   * - Fetch or create a "landing workspace"
   * - Remove invite token if used
   * - Return `{ landingWorkspace }`
   */
  const onSignUpSuccess = useCallback(
    async (
      auth: any,
      workspaceInviteToken?: string,
    ): Promise<{
      landingWorkspace?: Workspace
      privateWorkspaces?: Workspace[]
      isLandingWorkspaceDefaultWorkspace?: boolean
    }> => {
      const createDefaultWorkspace = async (auth: any, existingWorkspaces: Workspace[]) => {
        try {
          const currentWorkspaceStore = store.getState().workspace.workspaces
          const companyName = auth?.display_name ? `${auth.display_name}'s Workspace` : 'My Workspace'

          const res = await client.postUserCompany(
            companyName,
            undefined,
            undefined,
            undefined,
            true,
            true,
            isCampaigns,
          )
          if (res.type !== SUCCESS_RESPONSE_TYPE) return undefined

          const { company } = res.data
          const newWorkspace = {
            ...companyDetailToWorkspace(company),
            is_virtual_marketer: true,
            is_campaigns: isCampaigns,
          }

          trackOnboardingWorkspaceCreated()

          await featureFlags.load()

          // Merge in new workspace to Redux auth
          dispatch(
            authMerge({
              auth: {
                ...auth,
                last_workspace_id: newWorkspace.id,
                company_id: newWorkspace.id,
              },
            }),
          )

          // Track email events after Redux auth update - user must have submitted and verified email to reach this point
          trackEmailSubmitted(auth.email)
          trackEmailVerified()

          const combined = [...existingWorkspaces, ...currentWorkspaceStore, newWorkspace]
          const completeSignupResponse = await UserClient.completeSignup()
          if (completeSignupResponse.type === SUCCESS_RESPONSE_TYPE) {
            trackEvent(SIGNED_UP_EVENT, undefined, {
              email: auth.email,
              almanac_user_id: auth.id,
            })
            dispatch(
              authMerge({
                auth: {
                  ...auth,
                  signed_up: true,
                  last_workspace_id: newWorkspace.id,
                  company_id: newWorkspace.id,
                },
              }),
            )
          }
          UserService.setWorkspaces(combined)
          UserService.setCurrentWorkspaceId(newWorkspace.id)
          const response = await client.getMemberProfile(newWorkspace.id, auth.id)
          if (response.type === SUCCESS_RESPONSE_TYPE) {
            // Add delay to ensure AUTHED_SESSION_EVENT from GtmAuthTracker is processed first
            // Impact.io script expects authed_session to be in dataLayer before trial_granted
            // Need longer delay to account for Redux authMerge, workspace ID setting, and React Query execution
            setTimeout(() => {
              trackEvent(TRIAL_GRANTED, response.data)
            }, 500)
          } else {
            reportError('Failed to get member profile for trial_granted tracking', {
              severity: 'error',
              metaData: response,
            })
          }
          return newWorkspace
        } catch (err) {
          reportError('Error creating default workspace', { severity: 'error', metaData: err })
          return undefined
        }
      }

      try {
        // 1) Store auth (cookie/local) + Redux
        UserService.setAuthCookie(auth)
        dispatch(authMerge({ auth }))

        // 2) Load existing user workspaces
        const workspacesResp = await client.getUserWorkspaces()
        if (workspacesResp.type !== SUCCESS_RESPONSE_TYPE) {
          reportError('Failed to load workspaces on sign-up', {
            severity: 'error',
            metaData: { workspacesResp },
          })
          return {}
        }
        const existingWorkspaces = workspacesResp.data.workspaces
        const privateWorkspaces = findPrivateWorkspaces(existingWorkspaces).filter((w) => !w.guest)
        UserService.setWorkspaces(existingWorkspaces)

        // 3) If user was invited, find that workspace
        let invitedWorkspace: Workspace | undefined
        if (workspaceInviteToken) {
          invitedWorkspace = existingWorkspaces.find((ws: Workspace) => ws.invitation_token === workspaceInviteToken)
          removeWorkspaceInviteToken()
        }

        // 4) Compute the landing workspace
        let landingWorkspace = invitedWorkspace
        let isLandingWorkspaceDefaultWorkspace = false

        if (!landingWorkspace) {
          // find any private workspace
          // If user has no private workspace, create one
          if (!privateWorkspaces.length) {
            // brand new user: create default workspace
            landingWorkspace = await createDefaultWorkspace(auth, existingWorkspaces)
            isLandingWorkspaceDefaultWorkspace = true
          } else {
            // or pick first private workspace
            landingWorkspace = privateWorkspaces[0]
          }
        }

        // If the chosen workspace is actually "Almanac core", we might not want that:
        if (landingWorkspace && isAlmanacCoreWorkspace(landingWorkspace.id)) {
          landingWorkspace = undefined
        }

        // If we do have a workspace, set current workspace ID
        if (landingWorkspace) {
          UserService.setCurrentWorkspaceId(landingWorkspace.id)
          queryClient.invalidateQueries(['user-preferences', landingWorkspace.id])
        }

        return { landingWorkspace, privateWorkspaces, isLandingWorkspaceDefaultWorkspace }
      } catch (err) {
        reportError('Error in onSignUpSuccess', { severity: 'error', metaData: err })
        return {}
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, queryClient],
  )

  return { onSignUpSuccess }
}
